import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge, Button, Dropdown, Field, MessageBar, MessageBarBody, Option, Spinner,
  Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow, Text, Textarea,
  makeStyles, tokens,
} from '@fluentui/react-components';
import {
  errorReportsService, type AdminErrorReport, type AdminErrorReportSummary, type ErrorReportStatus,
} from '../api';
import { useTopBarBreadcrumbs, useTopBarTitle } from '../contexts/TopBarContext';
import AdminPageLayout, { AdminPageContent } from '../components/AdminPageLayout';
import { AdminDataGrid, AdminDataGridEmpty, AdminPagination } from '../components/AdminDataGrid';

const PAGE_SIZE = 25;
const statuses: ErrorReportStatus[] = ['open', 'investigating', 'resolved', 'dismissed'];
const useStyles = makeStyles({
  toolbar: { display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '16px' },
  filter: { minWidth: '160px' },
  details: { display: 'grid', gap: '8px', padding: '12px', backgroundColor: tokens.colorNeutralBackground2 },
  technical: { color: tokens.colorNeutralForeground3, overflowWrap: 'anywhere' },
  diagnosticGrid: { display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '4px 12px' },
  stack: {
    margin: 0, padding: '12px', maxHeight: '240px', overflow: 'auto', whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere', backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`, fontFamily: 'monospace', fontSize: '12px',
  },
  actions: { display: 'flex', gap: '8px', marginTop: '8px' },
  retentionNote: {
    display: 'block',
    marginTop: '16px',
    color: tokens.colorNeutralForeground3,
    textAlign: 'left',
  },
});

const AdminErrorReportsPage: React.FC = () => {
  const styles = useStyles();
  const [reports, setReports] = useState<AdminErrorReportSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<ErrorReportStatus | 'all'>('all');
  const [selected, setSelected] = useState<AdminErrorReport>();
  const [status, setStatus] = useState<ErrorReportStatus>('open');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingDetailsId, setLoadingDetailsId] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  useTopBarTitle('Error reports');
  useTopBarBreadcrumbs([{ text: 'Admin' }, { text: 'Error reports' }]);

  const load = useCallback(async () => {
    setLoading(true); setError(undefined);
    try {
      const result = await errorReportsService.list(page, PAGE_SIZE, filter === 'all' ? undefined : filter);
      setReports(result.reports); setTotal(result.total);
    } catch { setError('Error reports could not be loaded.'); }
    finally { setLoading(false); }
  }, [filter, page]);
  useEffect(() => { void load(); }, [load]);

  const choose = async (report: AdminErrorReportSummary) => {
    setSelected(undefined); setLoadingDetailsId(report.id); setError(undefined);
    try {
      const detail = await errorReportsService.get(report.id);
      setSelected(detail); setStatus(detail.status); setNote(detail.adminNote ?? '');
    } catch { setError('The error report details could not be loaded.'); }
    finally { setLoadingDetailsId(undefined); }
  };
  const save = async () => {
    if (!selected) return;
    setSaving(true); setError(undefined);
    try { await errorReportsService.update(selected.id, status, note || undefined); await load(); }
    catch { setError('The error report update could not be saved.'); }
    finally { setSaving(false); }
  };

  return (
    <AdminPageLayout>
      <AdminPageContent>
      <div className={styles.toolbar}>
        <Text weight="semibold">Status</Text>
        <Dropdown className={styles.filter} aria-label="Status" value={filter} selectedOptions={[filter]} onOptionSelect={(_, data) => { setFilter(data.optionValue as ErrorReportStatus | 'all'); setPage(1); }}>
          <Option value="all">all</Option>{statuses.map((item) => <Option key={item} value={item}>{item}</Option>)}
        </Dropdown>
        {loading && <Spinner size="tiny" />}
      </div>
      {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
      <AdminDataGrid><Table aria-label="User error reports">
        <TableHeader><TableRow><TableHeaderCell>Created</TableHeaderCell><TableHeaderCell>User</TableHeaderCell><TableHeaderCell>Status</TableHeaderCell><TableHeaderCell>Summary</TableHeaderCell></TableRow></TableHeader>
        <TableBody>{reports.map((report) => (
          <React.Fragment key={report.id}>
            <TableRow
              aria-expanded={selected?.id === report.id}
              onClick={() => { void choose(report); }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  void choose(report);
                }
              }}
              style={{ cursor: 'pointer' }}
              tabIndex={0}
            >
              <TableCell>{new Date(report.createdAt).toLocaleString()}</TableCell><TableCell>{report.user.name || report.user.email}</TableCell>
              <TableCell><Badge>{report.status}</Badge></TableCell><TableCell>{report.description.slice(0, 120)}</TableCell>
            </TableRow>
            {selected?.id === report.id && <TableRow><TableCell colSpan={4}><div className={styles.details}>
              <Text weight="semibold">Report {report.id}</Text><Text>{report.description}</Text>
              {report.reproductionSteps && <Text>Steps: {report.reproductionSteps}</Text>}
              <div className={styles.diagnosticGrid}>
                <Text weight="semibold">Error</Text><Text className={styles.technical}>{report.errorName || 'purged'}: {report.errorMessage || 'purged'}</Text>
                <Text weight="semibold">Support IDs</Text><Text className={styles.technical}>Error {report.errorId || 'none'} · Correlation {report.correlationId || 'none'}</Text>
                <Text weight="semibold">Route</Text><Text className={styles.technical}>{report.route || 'purged'}</Text>
                <Text weight="semibold">Network</Text><Text className={styles.technical}>{report.networkMethod || 'none'} {report.networkPath || ''} · status {report.networkStatus ?? 'none'} · {report.networkErrorType || 'none'}</Text>
              </div>
              {selected.stackTrace && <><Text weight="semibold">JavaScript stack</Text><pre className={styles.stack}>{selected.stackTrace}</pre></>}
              {selected.componentStack && <><Text weight="semibold">React component stack</Text><pre className={styles.stack}>{selected.componentStack}</pre></>}
              <Field label="Report status"><Dropdown value={status} selectedOptions={[status]} onOptionSelect={(_, data) => setStatus(data.optionValue as ErrorReportStatus)}>{statuses.map((item) => <Option key={item} value={item}>{item}</Option>)}</Dropdown></Field>
              <Field label="Internal note"><Textarea value={note} maxLength={4000} rows={3} onChange={(_, data) => setNote(data.value)} /></Field>
              <div className={styles.actions}><Button appearance="primary" disabled={saving} onClick={save}>{saving ? <Spinner size="tiny" /> : 'Save'}</Button></div>
            </div></TableCell></TableRow>}
            {loadingDetailsId === report.id && <TableRow><TableCell colSpan={4}><Spinner size="tiny" label="Loading report details" /></TableCell></TableRow>}
          </React.Fragment>
        ))}</TableBody>
      </Table></AdminDataGrid>
      {!loading && reports.length === 0 && <AdminDataGridEmpty>No reports match this filter.</AdminDataGridEmpty>}
      <AdminPagination totalLabel={`${total} reports`} page={page} pageSize={PAGE_SIZE} total={total} loading={loading} onPageChange={setPage} />
      <Text className={styles.retentionNote}>Reports are retained for 90 days. Technical diagnostics are removed after 30 days.</Text>
      </AdminPageContent>
    </AdminPageLayout>
  );
};

export default AdminErrorReportsPage;